/**
 * View component for /admin/authoring/studies
 *
 * Generic study list view. Defaults to 'all' with:
 * this.props.dispatch(studyActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import actions
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../studyActions';

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import TrainingManagementLayout from '../../admin/components/TrainingManagementLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import study components
import AdminStudyListItem from '../components/AdminStudyListItem.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';

// import study css modules
import studyStyles from '../studyModuleStyles.css';

class AdminStudyList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      confirmSendEmailsModalOpen: false
      , perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_sendRemindersForAllStudies'
      , '_setPerPage'
    );
  }

  componentDidMount() {

    // const userListUrl = `${window.hubUrl}/super/groups`
    // window.location.assign(userListUrl);
    
    const { dispatch } = this.props;
    // fetch a list of your choice
    dispatch(studyActions.fetchListIfNeeded('all')); // defaults to 'all'
    dispatch(studyActions.setFilter({query: '', sortBy: 'name'}));
    dispatch(studyActions.setPagination({page: 1, per: 50}));
    dispatch(sponsorActions.fetchListIfNeeded('all'));
  }

  _handleSort(sortBy) {
    const { dispatch, match, studyList } = this.props;
    let newFilter = studyList.filter;
    if(studyList.filter.sortBy && studyList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(studyActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(studyActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, studyList } = this.props;
    let newPagination = studyList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(studyActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(studyActions.setPagination(newPagination));
  }

  _handleQuery(e) {
    const { dispatch, match, studyList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = studyList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(studyActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: studyList.filter.sortBy
    }));
  }

  _sendRemindersForAllStudies() {
    const { dispatch } = this.props;
    dispatch(sponsorActions.sendRemindersBySponsor({id: 'all'}))
    this.setState({
      confirmSendEmailsModalOpen: false
    });
  }

  render() {
    const {
      location
      , paginatedList
      , sortedList
      , sponsorList
      , sponsorMap
      , studyList
      , studyStore
    } = this.props;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = (
      !sponsorList
      || sponsorList.length < 1
      || !studyList
      || !studyList.items
      || studyList.didInvalidate
    );
    const sortedEmpty = sortedList.length === 0;

    return (
      <TrainingManagementLayout>
        <Helmet
          title={"View Study | Pro-ficiency"}
        />
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <h1> Programs</h1>
              <hr/>
              { isEmpty ?
                (
                  studyList && studyList.isFetching ?
                  <div className="u-centerText">
                    <div className="loading"></div>
                  </div>
                  :
                  <h2>Empty.</h2>
                )
                :
                <div style={{ opacity: studyList.isFetching ? 0.5 : 1 }}>
                  <div className="yt-toolbar">
                    <div className="yt-tools right">
                      <div className="search">
                        <SearchInput
                          name="query"
                          value={studyList.filter.query}
                          change={this._handleQuery}
                          placeholder="Search..."
                          required={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="content-container">
                    <div className="table-wrapper">
                      <table className="yt-table striped">
                        <caption>
                          { studyList.filter && studyList.filter.query && studyList.filter.query.length > 0 ?
                            <span>Filtered Programs &mdash; {sortedList.length} of {Object.keys(studyList.items).length}</span>
                            :
                            <span>All Programs &mdash; {Object.keys(studyList.items).length}</span>
                          }
                          <div className="per-page-select u-pullRight">
                            <label>Show per page: </label>
                            <select
                              name="numPerPage"
                              onChange={(e) => this._setPerPage(e.target.value)}
                              value={this.state.perPage}
                              >
                              <option value={25}> 25 </option>
                              <option value={50}> 50 </option>
                              <option value={100}> 100 </option>
                            </select>
                          </div>
                        </caption>
                        <thead>
                          <tr>
                            <th className="_40 sortable" onClick={()=> this._handleSort('name')}>
                              Name
                              {studyList.filter.sortBy === '-name' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyList.filter.sortBy === 'name' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20 sortable" onClick={() => this._handleSort('sponsorName')}>
                              Sponsor
                              {studyList.filter.sortBy === '-sponsorName' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyList.filter.sortBy === 'sponsorName' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20">Quick Links</th>
                            <th className="_20 sortable" onClick={()=> this._handleSort('updated')}>
                              Last Modified
                              {studyList.filter.sortBy === 'updated' ?
                                <i className="ion ion-ios-arrow-up" />
                                : studyList.filter.sortBy === '-updated' ?
                                <i className="ion ion-ios-arrow-down" />
                                : null
                              }
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            paginatedList.map((study, i) =>
                            <AdminStudyListItem
                              key={study._id}
                              sponsor={sponsorMap[study._organization]}
                              study={study}
                            />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                      null
                      :
                      <Pagination
                        pagination={studyList.pagination}
                        setPagination={this._handleSetPagination}
                        totalPages={ Math.floor(sortedList.length / studyList.pagination.per) + 1}
                      />
                    }
                  </div>
                </div>
              </div>
            }
          </div>
          <AlertModal
            alertMessage={<div> Are you <em>sure</em> you want to send an email to all active program users for <strong>all programs</strong> that have reminders turned on?</div> }
            alertTitle="Send reminders for ALL programs"
            closeAction={() => this.setState({confirmSendEmailsModalOpen: false})}
            confirmAction={this._sendRemindersForAllStudies}
            confirmText="Yes, send emails"
            declineText="Never mind"
            isOpen={this.state.confirmSendEmailsModalOpen}
            type="warning"
          />
        </section>
      </div>
      </TrainingManagementLayout>
    )
  }
}

AdminStudyList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const studyStore = store.study;
  const studyList = studyStore.lists ? studyStore.lists.all : null;
  const sponsorList = store.sponsor.util.getList() || [];
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (studyList) {

    const pagination = studyList.pagination;
    const filter = studyList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = studyList && studyList.items ? studyList.items.filter((studyId) => {
      return filterUtils.filterStudy(queryTestString, studyStore.byId[studyId], sponsorList);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = studyStore.byId[item];
      newItem.sponsorName = store.sponsor.byId[newItem._organization] ? store.sponsor.byId[newItem._organization].name : '';
      return newItem;
    });
    switch(sortBy) {
      case 'name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
        break;
      case '-name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['desc']);
        break;
      case 'sponsorName':
        sortedList = _.orderBy(sortedList, [item => item.sponsorName.toLowerCase()], ['asc']);
        break;
      case '-sponsorName':
        sortedList = _.orderBy(sortedList, [item => item.sponsorName.toLowerCase()], ['desc']);
        break;
      case 'updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    paginatedList
    , sortedList
    , sponsorList
    , sponsorMap: store.sponsor.byId
    , studyList
    , studyStore
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminStudyList)
);



// WEBPACK FOOTER //
// modules/study/views/AdminStudyList.js.jsx